import React, { useId, useRef, useLayoutEffect, useState, useMemo, useEffect } from "react";
import "./LeafInput.scss";
import LeafRectangleLoader from "../LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { storedLocale, marketCode } from "../../../components/Authentication/util";
import { getNumberFormatter } from "../../util/format";
import { noDecimalMarkets } from "../../../components/Lit/acc-mixins/src/modules/l10n/constants.js";

/**
 * Props interface for LeafInput component
 */
export interface LeafInputProps {
  value: number | "";
  onChange: (value: number | "") => void;
  suffix?: string;
  min?: number;
  max?: number;
  placeholder?: string;
  allowDecimal?: boolean;
  countryCode?: string;
  isLoading: boolean;
  disabled?: boolean;
  inputElementProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

/**
 * Numeric input component with suffix support and locale-aware formatting
 */
const LeafInput = ({
  value,
  onChange,
  suffix,
  min,
  max,
  placeholder,
  allowDecimal = true,
  countryCode,
  isLoading,
  disabled,
  inputElementProps,
}: LeafInputProps) => {
  // State management
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<string>("");
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [inputWidth, setInputWidth] = useState<string>("100%");

  // Refs for DOM measurements
  const suffixRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  // Locale and formatting setup
  const locale = storedLocale();
  const market = marketCode();
  const inputFormatter = useMemo(() => getNumberFormatter(locale, market), [locale, market]);

  // Decimal configuration based on country
  const decimalLimit = !allowDecimal || noDecimalMarkets.includes(countryCode || "") ? 0 : 2;

  // Helper functions
  const getInputPattern = (limit: number) => (limit > 0 ? `^\\d*(\\.\\d{0,${limit}})?$` : "^\\d*$");
  const isValidNumber = (val: string) => /^(\d+)?(\.\d+)?$/.test(val);
  const getFallbackValue = () => (typeof min === "number" ? min : "");

  /**
   * Sync external value with internal state when not focused
   */
  useEffect(() => {
    if (!isFocused) {
      const formatted = value === "" ? "" : inputFormatter.format(value);
      setInternalValue(formatted);
    }
  }, [value, isFocused, inputFormatter]);

  /**
   * Detect suffix overflow and size input to content
   */
  useLayoutEffect(() => {
    if (measureRef.current && suffixRef.current) {
      const inputTextWidth = measureRef.current.offsetWidth;
      const suffixWidth = suffixRef.current.offsetWidth;
      const wrapperWidth = measureRef.current.parentElement?.offsetWidth ?? 0;

      const buffer = 4;
      const totalRequiredWidth = inputTextWidth + suffixWidth + buffer;

      setIsOverflowing(totalRequiredWidth > wrapperWidth);
      setInputWidth(`${Math.min(inputTextWidth + buffer, wrapperWidth)}px`);
    }
  }, [internalValue, suffix]);

  /**
   * Clamp number within min/max bounds
   */
  const clampValue = (num: number) => {
    if (typeof min === "number" && num < min) return min;
    if (typeof max === "number" && num > max) return max;
    return num;
  };

  /**
   * Remove invalid characters and enforce decimal limits
   */
  const sanitizeValue = (input: string): string => {
    const digitsOnly = input.replace(/[^0-9.]/g, "");
    if (decimalLimit === 0) return digitsOnly.split(".")[0];

    const match = digitsOnly.match(new RegExp(`^(\\d+)?(\\.\\d{0,${decimalLimit}})?`));
    if (!match) return "";
    const [, intPart = "", decimalPart = ""] = match;
    return intPart + decimalPart;
  };

  /**
   * Convert sanitized string to number with validation
   */
  const stringToNumberOrEmpty = (s: string): number | "" => {
    if (s === "" || !isValidNumber(s)) return getFallbackValue();
    const n = Number(s);
    return isNaN(n) ? getFallbackValue() : clampValue(n);
  };

  /**
   * Handle input changes during typing (no validation/onChange call)
   */
  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (disabled) return;
    const raw = e.target.value;
    const sanitized = sanitizeValue(raw);
    setInternalValue(sanitized);
  };

  /**
   * Apply formatting and trigger onChange with final value
   */
  const applyFormatted = (input: string) => {
    const sanitized = sanitizeValue(input);
    const finalNumber = stringToNumberOrEmpty(sanitized);
    const formatted = finalNumber === "" ? "" : inputFormatter.format(finalNumber);
    setInternalValue(formatted);

    // Only trigger onChange if value actually changed
    const isEqual =
      typeof finalNumber === "number" && typeof value === "number"
        ? Math.abs(finalNumber - value) < 0.01
        : finalNumber === value;

    if (!isEqual) {
      onChange(finalNumber);
    }
  };

  /**
   * Handle input blur - apply formatting and validation
   */
  const handleBlur = () => {
    if (!disabled) {
      setIsFocused(false);
      applyFormatted(internalValue);
    }
  };

  /**
   * Handle paste events with normalization
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!disabled) {
      e.preventDefault();
      const pasted = e.clipboardData.getData("Text").normalize("NFKC");
      applyFormatted(pasted);
    }
  };

  /**
   * Handle input focus
   */
  const handleFocus = () => !disabled && setIsFocused(true);

  /**
   * Handle keyboard events (Enter to blur)
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const generatedId = useId();
  const inputId = inputElementProps?.id || generatedId;

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- label wraps input via LeafRectangleLoader
    <label htmlFor={inputId} className={`input-suffix-wrapper${disabled ? " input-suffix-wrapper--disabled" : ""}`}>
      <LeafRectangleLoader isLoading={isLoading} height="1.3rem" width="100%">
        <div className="input-suffix-field-wrapper">
          {/* Hidden measure text */}
          <span className="input-suffix-measure" ref={measureRef} aria-hidden="true">
            {internalValue === "" ? placeholder : internalValue}
          </span>

          <input
            {...inputElementProps}
            id={inputId}
            type="text"
            inputMode={decimalLimit > 0 ? "decimal" : "numeric"}
            pattern={getInputPattern(decimalLimit)}
            min={min}
            max={max}
            value={internalValue}
            className="input-suffix-input"
            style={{ width: inputWidth }}
            aria-label={placeholder ? placeholder + (suffix ? ` (${suffix})` : "") : suffix}
            autoComplete="off"
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />

          {suffix && internalValue !== "" && (
            <span
              className={`input-suffix-suffix${isOverflowing ? " input-suffix-suffix--fixed" : ""}`}
              ref={suffixRef}
              aria-hidden="true"
            >
              {suffix}
            </span>
          )}
        </div>

        <span
          className={
            "input-suffix-label" +
            (isFocused || internalValue || disabled ? " input-suffix-label--float" : "") +
            (disabled ? " input-suffix-label--disabled" : "")
          }
        >
          {placeholder}
        </span>
      </LeafRectangleLoader>
    </label>
  );
};

export default LeafInput;
